<script lang="ts" setup>
import { computed, PropType, ref } from 'vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import NewJobButton from '@/components/common/NewJobButton.vue'
import { useBulkUpdate } from '@/components/jobs/bulk-update'
import moment from 'moment/moment'
import { useIntervalFn } from '@vueuse/core'
import { useCurrentOrg } from '@/common/useCurrentOrg'
import JobFilterSets from '@/components/jobs/job-search-page/JobFilterSets.vue'
import { useNavStore } from '@/components/nav/navStore'
import { useJobSearchRouteContext } from '@/common/useJobSearchRouteContext'

const props = defineProps({
  isMobileView: {
    type: Boolean as PropType<boolean>,
    required: true,
  },
  lastFetchTime: {
    type: Number as PropType<number>,
    required: true,
  },
  isFetching: {
    type: Boolean as PropType<boolean>,
    required: true,
  },
  selectedJobs: {
    type: Array as PropType<any[]>,
    required: true,
  },
  refreshError: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
})

const { currentOrg } = useCurrentOrg()
const navStore = useNavStore()
const { parentRouteShortName } = useJobSearchRouteContext()

const lastFetchedTimeAgo = ref<string>('')
const lastFetchedMoment = computed(() => {
  return moment(props.lastFetchTime)
})

useIntervalFn(() => {
  lastFetchedTimeAgo.value = lastFetchedMoment.value.fromNow()
}, 1000)

const emit = defineEmits<{
  (e: 'refresh'): void
  (e: 'clear-selection'): void
  (e: 'open-bulk-update'): void
}>()

const bulkUpdateButtonLabel = computed(
  () => `Update ${props.selectedJobs.length} ${props.selectedJobs.length >= 2 ? 'jobs' : 'job'}`,
)

const handleRefresh = () => {
  emit('refresh')
}

const handleClearSelection = () => {
  emit('clear-selection')
}

const handleOpenBulkUpdate = () => {
  emit('open-bulk-update')
}
</script>

<template>
  <v-row align="center" dense no-gutters class="flex-nowrap">
    <v-col cols="auto">
      <vs-heading v-if="!isMobileView" type="title" class="jobs-header">
        {{ currentOrg?.name }} Jobs
      </vs-heading>

      <vs-button
        v-if="isMobileView"
        class="overlay-button"
        :icon="navStore.isNavVisible ? 'mdi-menu-open' : 'mdi-menu'"
        type="secondary"
        @click="navStore.toggleNav"
      />
    </v-col>

    <v-col class="flex-grow-1 min-width-0">
      <job-filter-sets />
    </v-col>

    <template v-if="!isMobileView">
      <v-col cols="auto" class="refresh-button">
        <v-tooltip bottom close-delay="100">
          <template #activator="{ on }">
            <vs-button
              :destructive="props.refreshError"
              :loading="isFetching"
              data-test="jobs-list-refresh"
              icon="refresh"
              type="secondary"
              @click="handleRefresh"
              v-on="on"
            />
          </template>
          <span>
            {{
              props.refreshError ? 'Last refresh failed - click to try again' : lastFetchedTimeAgo
            }}
          </span>
        </v-tooltip>
      </v-col>

      <v-col v-if="!(selectedJobs.length > 0 && useBulkUpdate().value)" cols="auto">
        <new-job-button
          :label="!isMobileView ? 'New job' : 'Job'"
          :parent-route-short-name="parentRouteShortName"
        />
      </v-col>
      <template v-else>
        <v-col cols="auto" class="pr-2">
          <vs-button
            :destructive="true"
            data-test="jobs-table-update-jobs"
            label="Clear selection"
            type="secondary"
            @click="handleClearSelection"
          />
        </v-col>
        <v-col cols="auto">
          <vs-button
            :label="bulkUpdateButtonLabel"
            data-test="jobs-table-row-clear"
            @click="handleOpenBulkUpdate"
          />
        </v-col>
      </template>
    </template>
  </v-row>
</template>

<style scoped>
:deep(.autocomplete fieldset) {
  border: 1px solid var(--color-grey--lighter);
}

:deep(.autocomplete:not(.v-input--is-focused):hover fieldset) {
  border-color: var(--color-grey--light);
}

:deep(.autocomplete.v-input--is-focused fieldset) {
  border: 1px solid var(--color-blue);
}

.spacer-sm {
  height: 8px;
}

.jobs-header {
  font-size: 22px !important;
}

.info-icon {
  background-color: transparent;
  padding-bottom: 8px;
  margin-bottom: -7px;
  padding-right: 12px;
  margin-right: -8px;
  padding-left: 12px;
  margin-left: -8px;
}

.info-icon::before,
.info-icon::after {
  background-color: transparent !important;
}

:deep(.info-card) {
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

.refresh-button {
  margin-right: 8px;
}

.min-width-0 {
  min-width: 0 !important;
}
</style>
